import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import {
  RefreshCw,
  CheckCircle2,
  Clock,
  AlertCircle,
  Trash2,
  Loader2,
} from 'lucide-react'
import { useLiveQuery } from 'dexie-react-hooks'
import { db, deleteDraft } from '@/lib/db'
import { useSync } from '@/hooks/useSync'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { SyncIndicator } from '@/components/shared/SyncIndicator'
import type { PickupRecordDraft } from '@/types'
import { getDraftStartedAt } from '@/lib/pickupDraftHelpers'

export function SyncPage() {
  const { isOnline, isSyncing, lastSyncAt, pendingCount, syncErrors, sync, refreshPendingCount } =
    useSync()

  const allDrafts = useLiveQuery(() => db.pickup_records_draft.toArray(), [])

  const [deletingId, setDeletingId] = useState<number | null>(null)

  const getDraftPhotoCount = (draft: PickupRecordDraft): number =>
    draft.items.reduce((sum, item) => sum + (item.photos?.length ?? 0), 0)

  useEffect(() => {
    refreshPendingCount()
  }, [refreshPendingCount])

  const handleDeleteDraft = async (draft: PickupRecordDraft) => {
    if (!draft.id) return
    setDeletingId(draft.id)
    try {
      await deleteDraft(draft.id)
      await refreshPendingCount()
    } catch (err) {
      console.error('Error eliminando draft:', err)
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Encabezado */}
      <div>
        <h1 className="text-2xl font-bold text-[#383f2d]">Sincronización</h1>
        <p className="text-sm text-[#7c8c43]">
          Estado de los registros locales pendientes de sincronizar con el servidor
        </p>
      </div>

      {/* Estado general */}
      <SyncIndicator
        isOnline={isOnline}
        syncState={{ isSyncing, lastSyncAt, pendingCount, syncErrors }}
        onSync={sync}
      />

      {/* Botón sincronizar manual */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Sincronización manual</CardTitle>
          <CardDescription>
            Fuerza la sincronización de todos los registros pendientes con el servidor
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full sm:w-auto bg-[#dff24b] text-[#383f2d] hover:bg-[#b9d94a] border-0 font-semibold" onClick={sync} disabled={!isOnline || isSyncing || pendingCount === 0}>
            {isSyncing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sincronizando...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sincronizar ahora
                {pendingCount > 0 && (
                  <Badge className="ml-2" variant="secondary">
                    {pendingCount}
                  </Badge>
                )}
              </>
            )}
          </Button>
          {!isOnline && (
            <p className="mt-2 text-xs text-[#7c8c43]">
              Sin conexión a internet. La sincronización ocurrirá automáticamente al recuperar la conexión.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Lista de drafts */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base">Registros locales</CardTitle>
            <Badge variant={pendingCount > 0 ? 'destructive' : 'secondary'}>
              {allDrafts?.length ?? 0} total
            </Badge>
          </div>
          <CardDescription>
            Registros guardados en este dispositivo
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!allDrafts || allDrafts.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <CheckCircle2 className="h-10 w-10 text-[#b9d94a] mb-3" />
              <p className="text-sm font-medium text-[#383f2d]">
                No hay registros locales
              </p>
              <p className="text-xs text-[#7c8c43] mt-1">
                Todos los registros han sido sincronizados con el servidor
              </p>
            </div>
          ) : (
            <ul className="divide-y">
              {allDrafts.map((draft) => (
                <li
                  key={draft.id}
                  className="flex flex-col gap-3 py-3 sm:flex-row sm:items-start"
                >
                  <div className="mt-0.5">
                    {draft.pending_sync ? (
                      <Clock className="h-4 w-4 text-[#b9d94a]" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-[#7c8c43]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#383f2d]">
                      Retiro #{draft.pickup_id}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Registrado:{' '}
                      {format(
                        new Date(getDraftStartedAt(draft)),
                        "d 'de' MMMM yyyy, HH:mm",
                        { locale: es }
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {draft.items.length} tipo
                      {draft.items.length !== 1 ? 's' : ''} de residuo
                      {getDraftPhotoCount(draft) > 0 &&
                        ` • ${getDraftPhotoCount(draft)} foto${getDraftPhotoCount(draft) !== 1 ? 's' : ''}`}
                    </p>
                    {draft.pending_sync ? (
                      <Badge
                        variant="secondary"
                        className="mt-1 text-[10px] bg-[#dff24b] text-[#383f2d]"
                      >
                        Pendiente de sincronizar
                      </Badge>
                    ) : (
                      <Badge
                        variant="secondary"
                        className="mt-1 text-[10px] bg-[#b9d94a] text-[#383f2d]"
                      >
                        Sincronizado (servidor #{draft.server_id})
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full justify-center text-red-400 hover:bg-red-50 hover:text-red-600 sm:h-8 sm:w-8 sm:shrink-0"
                    onClick={() => handleDeleteDraft(draft)}
                    disabled={deletingId === draft.id}
                  >
                    {deletingId === draft.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Errores de sincronización */}
      {syncErrors.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              Errores de sincronización
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {syncErrors.map((err, i) => (
                <li key={i} className="text-xs text-red-600">
                  <span className="font-semibold block">{err.recordLabel}</span>
                  <span>{err.message}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
